import React, { useEffect, useState } from "react";
import { Save, ExternalLink, Globe, Instagram, Twitter, Linkedin, Facebook, Plus, Trash2, MoveUp, MoveDown, Briefcase, Quote } from "lucide-react";
import api from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import { toast } from "sonner";

const INPUT_CLASS = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none";

const newId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

const SOCIAL_FIELDS = [
  { key: "website", icon: Globe, label: "Website", placeholder: "https://your-studio.com" },
  { key: "instagram", icon: Instagram, label: "Instagram", placeholder: "https://instagram.com/handle" },
  { key: "twitter", icon: Twitter, label: "Twitter / X", placeholder: "https://twitter.com/handle" },
  { key: "linkedin", icon: Linkedin, label: "LinkedIn", placeholder: "https://linkedin.com/in/you" },
  { key: "facebook", icon: Facebook, label: "Facebook", placeholder: "https://facebook.com/your-page" },
];

const EMPTY = {
  hero_image_url: "",
  hero_title: "",
  hero_description: "",
  contact_email: "",
  contact_phone: "",
  contact_whatsapp: "",
  contact_address: "",
  socials: { website: "", instagram: "", twitter: "", linkedin: "", facebook: "" },
  portfolio_items: [],
  testimonials: [],
};

export default function PublicPage() {
  const { agency } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const publicUrl = agency?.subdomain
    ? `${window.location.origin}/a/${agency.subdomain}`
    : "";

  const load = async () => {
    try {
      if (!agency?.subdomain) return;
      const { data } = await api.get(`/portal/agency/${agency.subdomain}`);
      const pp = data.public_page || {};
      setForm({
        ...EMPTY,
        ...pp,
        socials: { ...EMPTY.socials, ...(pp.socials || {}) },
        portfolio_items: pp.portfolio_items || [],
        testimonials: pp.testimonials || [],
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency?.subdomain]);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      // strip empty strings to null for cleanliness
      const payload = {
        ...form,
        hero_image_url: form.hero_image_url || null,
        hero_title: form.hero_title || null,
        hero_description: form.hero_description || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        contact_whatsapp: form.contact_whatsapp || null,
        contact_address: form.contact_address || null,
        socials: Object.fromEntries(
          Object.entries(form.socials).map(([k, v]) => [k, v || null]),
        ),
      };
      await api.patch("/agency/public-page", payload);
      toast.success("Public page saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-12 text-slate-400">Loading…</div>;
  }

  const updatePortfolio = (idx, patch) => {
    setForm((f) => ({
      ...f,
      portfolio_items: f.portfolio_items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };
  const removePortfolio = (idx) => {
    setForm((f) => ({
      ...f,
      portfolio_items: f.portfolio_items.filter((_, i) => i !== idx),
    }));
  };
  const movePortfolio = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= form.portfolio_items.length) return;
    setForm((f) => {
      const arr = [...f.portfolio_items];
      const [item] = arr.splice(idx, 1);
      arr.splice(target, 0, item);
      return { ...f, portfolio_items: arr };
    });
  };

  const updateTestimonial = (idx, patch) => {
    setForm((f) => ({
      ...f,
      testimonials: f.testimonials.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };
  const removeTestimonial = (idx) => {
    setForm((f) => ({
      ...f,
      testimonials: f.testimonials.filter((_, i) => i !== idx),
    }));
  };
  const moveTestimonial = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= form.testimonials.length) return;
    setForm((f) => {
      const arr = [...f.testimonials];
      const [item] = arr.splice(idx, 1);
      arr.splice(target, 0, item);
      return { ...f, testimonials: arr };
    });
  };

  return (
    <div className="p-8 md:p-12 animate-fade-up max-w-4xl">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <p className="eyebrow mb-2">Marketing</p>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="public-page-title">Public Page</h1>
          <p className="text-slate-500 text-sm mt-2">
            This is the public landing page your tenants land on at{" "}
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-900 font-mono underline hover:no-underline"
              data-testid="public-page-url"
            >
              {publicUrl}
            </a>
          </p>
        </div>
        <div className="flex gap-2">
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium border border-slate-200 hover:bg-slate-100 rounded-full px-5 py-2.5 active:scale-95"
            data-testid="preview-public-page"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Preview
          </a>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-700 rounded-full px-5 py-2.5 active:scale-95 disabled:opacity-50"
            data-testid="save-public-page"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <form onSubmit={save} className="space-y-6">
        {/* Hero */}
        <section className="bento p-8" data-testid="hero-editor">
          <p className="eyebrow mb-2">Hero section</p>
          <h2 className="text-2xl font-bold tracking-tight mb-1">Make a first impression.</h2>
          <p className="text-slate-500 text-sm mb-6">A big title, a one-line pitch, and an image.</p>

          <FormRow label="Hero title">
            <input
              value={form.hero_title}
              onChange={(e) => setForm({ ...form, hero_title: e.target.value })}
              placeholder={`Welcome to ${agency?.name || "your agency"}`}
              className={INPUT_CLASS}
              data-testid="hero-title"
            />
          </FormRow>

          <FormRow label="Hero description">
            <textarea
              rows={3}
              value={form.hero_description}
              onChange={(e) => setForm({ ...form, hero_description: e.target.value })}
              placeholder="A short, punchy sentence about what your agency does."
              className={`${INPUT_CLASS} resize-none`}
              data-testid="hero-description"
            />
          </FormRow>

          <FormRow
            label="Hero image URL"
            hint="Direct link to a JPG/PNG. Use Cloudinary, Imgur, or any image host."
          >
            <input
              type="url"
              value={form.hero_image_url}
              onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })}
              placeholder="https://example.com/your-hero.jpg"
              className={INPUT_CLASS}
              data-testid="hero-image-url"
            />
          </FormRow>

          {form.hero_image_url && (
            <div className="mt-2">
              <img
                src={form.hero_image_url}
                alt="Hero preview"
                className="w-full max-w-md aspect-[4/3] object-cover rounded-2xl border border-slate-200"
                data-testid="hero-image-preview"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          )}
        </section>

        {/* Contact */}
        <section className="bento p-8" data-testid="contact-editor">
          <p className="eyebrow mb-2">Contact</p>
          <h2 className="text-2xl font-bold tracking-tight mb-6">How can people reach you?</h2>
          <FormRow label="Contact email">
            <input
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="hello@your-studio.com"
              className={INPUT_CLASS}
              data-testid="contact-email-input"
            />
          </FormRow>
          <FormRow label="Phone">
            <input
              type="tel"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              placeholder="+39 351 21 82 553"
              className={INPUT_CLASS}
              data-testid="contact-phone-input"
            />
          </FormRow>
          <FormRow label="WhatsApp" hint="With country code, e.g. +393512182553">
            <input
              type="tel"
              value={form.contact_whatsapp}
              onChange={(e) => setForm({ ...form, contact_whatsapp: e.target.value })}
              placeholder="+39 351 21 82 553"
              className={INPUT_CLASS}
              data-testid="contact-whatsapp-input"
            />
          </FormRow>
          <FormRow label="Address">
            <input
              value={form.contact_address}
              onChange={(e) => setForm({ ...form, contact_address: e.target.value })}
              placeholder="Via Roma 1, Milano, Italy"
              className={INPUT_CLASS}
              data-testid="contact-address-input"
            />
          </FormRow>
        </section>

        {/* Socials */}
        <section className="bento p-8" data-testid="socials-editor">
          <p className="eyebrow mb-2">Social links</p>
          <h2 className="text-2xl font-bold tracking-tight mb-6">Where else can clients find you?</h2>
          {SOCIAL_FIELDS.map((s) => (
            <FormRow key={s.key} label={s.label} icon={<s.icon className="w-3.5 h-3.5" />}>
              <input
                type="url"
                value={form.socials[s.key] || ""}
                onChange={(e) =>
                  setForm({ ...form, socials: { ...form.socials, [s.key]: e.target.value } })
                }
                placeholder={s.placeholder}
                className={INPUT_CLASS}
                data-testid={`social-${s.key}-input`}
              />
            </FormRow>
          ))}
        </section>

        {/* Portfolio */}
        <section className="bento p-8" data-testid="portfolio-editor">
          <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
            <div>
              <p className="eyebrow mb-2">Portfolio</p>
              <h2 className="text-2xl font-bold tracking-tight mb-1">Showcase your work.</h2>
              <p className="text-slate-500 text-sm">Each card opens an external link in a new tab.</p>
            </div>
            <button
              type="button"
              onClick={() =>
                setForm({
                  ...form,
                  portfolio_items: [
                    ...form.portfolio_items,
                    { id: newId(), name: "", image_url: "", link: "" },
                  ],
                })
              }
              className="inline-flex items-center gap-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-700 rounded-full px-5 py-2.5 active:scale-95"
              data-testid="add-portfolio-item"
            >
              <Plus className="w-4 h-4" /> Add project
            </button>
          </div>

          {form.portfolio_items.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm border border-dashed border-slate-200 rounded-2xl" data-testid="portfolio-empty">
              <Briefcase className="w-6 h-6 mx-auto mb-2 opacity-60" />
              No projects yet. Click "Add project" to add your first one.
            </div>
          ) : (
            <div className="space-y-4" data-testid="portfolio-list">
              {form.portfolio_items.map((item, idx) => (
                <div key={item.id} className="border border-slate-200 rounded-2xl p-5 grid md:grid-cols-[120px_1fr_auto] gap-4 items-start" data-testid={`portfolio-row-${idx}`}>
                  <div className="w-full md:w-[120px] aspect-square rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name || "preview"}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <Briefcase className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <div className="space-y-3 min-w-0">
                    <input
                      value={item.name}
                      onChange={(e) => updatePortfolio(idx, { name: e.target.value })}
                      placeholder="Project name"
                      className={INPUT_CLASS}
                      data-testid={`portfolio-name-${idx}`}
                    />
                    <input
                      type="url"
                      value={item.image_url || ""}
                      onChange={(e) => updatePortfolio(idx, { image_url: e.target.value })}
                      placeholder="Image URL (https://…)"
                      className={INPUT_CLASS}
                      data-testid={`portfolio-image-${idx}`}
                    />
                    <input
                      type="url"
                      value={item.link || ""}
                      onChange={(e) => updatePortfolio(idx, { link: e.target.value })}
                      placeholder="External link (https://…)"
                      className={INPUT_CLASS}
                      data-testid={`portfolio-link-${idx}`}
                    />
                  </div>
                  <div className="flex md:flex-col gap-1 justify-end">
                    <button
                      type="button"
                      onClick={() => movePortfolio(idx, -1)}
                      disabled={idx === 0}
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-30"
                      aria-label="Move up"
                      data-testid={`portfolio-move-up-${idx}`}
                    >
                      <MoveUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => movePortfolio(idx, 1)}
                      disabled={idx === form.portfolio_items.length - 1}
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-30"
                      aria-label="Move down"
                      data-testid={`portfolio-move-down-${idx}`}
                    >
                      <MoveDown className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removePortfolio(idx)}
                      className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                      aria-label="Delete"
                      data-testid={`portfolio-delete-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Testimonials */}
        <section className="bento p-8" data-testid="testimonials-editor">
          <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
            <div>
              <p className="eyebrow mb-2">Testimonials</p>
              <h2 className="text-2xl font-bold tracking-tight mb-1">Social proof.</h2>
              <p className="text-slate-500 text-sm">A few words from happy clients go a long way.</p>
            </div>
            <button
              type="button"
              onClick={() =>
                setForm({
                  ...form,
                  testimonials: [
                    ...form.testimonials,
                    { id: newId(), name: "", title: "", image_url: "", quote: "" },
                  ],
                })
              }
              className="inline-flex items-center gap-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-700 rounded-full px-5 py-2.5 active:scale-95"
              data-testid="add-testimonial"
            >
              <Plus className="w-4 h-4" /> Add testimonial
            </button>
          </div>

          {form.testimonials.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm border border-dashed border-slate-200 rounded-2xl" data-testid="testimonials-empty">
              <Quote className="w-6 h-6 mx-auto mb-2 opacity-60" />
              No testimonials yet. Click "Add testimonial" to add your first one.
            </div>
          ) : (
            <div className="space-y-4" data-testid="testimonials-list">
              {form.testimonials.map((item, idx) => (
                <div key={item.id} className="border border-slate-200 rounded-2xl p-5 grid md:grid-cols-[80px_1fr_auto] gap-4 items-start" data-testid={`testimonial-row-${idx}`}>
                  <div className="w-20 h-20 rounded-full bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name || "avatar"}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <Quote className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <div className="space-y-3 min-w-0">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <input
                        value={item.name}
                        onChange={(e) => updateTestimonial(idx, { name: e.target.value })}
                        placeholder="Name"
                        className={INPUT_CLASS}
                        data-testid={`testimonial-name-${idx}`}
                      />
                      <input
                        value={item.title || ""}
                        onChange={(e) => updateTestimonial(idx, { title: e.target.value })}
                        placeholder="Title / Company"
                        className={INPUT_CLASS}
                        data-testid={`testimonial-title-${idx}`}
                      />
                    </div>
                    <input
                      type="url"
                      value={item.image_url || ""}
                      onChange={(e) => updateTestimonial(idx, { image_url: e.target.value })}
                      placeholder="Avatar image URL (https://…)"
                      className={INPUT_CLASS}
                      data-testid={`testimonial-image-${idx}`}
                    />
                    <textarea
                      rows={3}
                      value={item.quote}
                      onChange={(e) => updateTestimonial(idx, { quote: e.target.value })}
                      placeholder="What they said…"
                      className={`${INPUT_CLASS} resize-none`}
                      data-testid={`testimonial-quote-${idx}`}
                    />
                  </div>
                  <div className="flex md:flex-col gap-1 justify-end">
                    <button
                      type="button"
                      onClick={() => moveTestimonial(idx, -1)}
                      disabled={idx === 0}
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-30"
                      aria-label="Move up"
                      data-testid={`testimonial-move-up-${idx}`}
                    >
                      <MoveUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveTestimonial(idx, 1)}
                      disabled={idx === form.testimonials.length - 1}
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-30"
                      aria-label="Move down"
                      data-testid={`testimonial-move-down-${idx}`}
                    >
                      <MoveDown className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeTestimonial(idx)}
                      className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                      aria-label="Delete"
                      data-testid={`testimonial-delete-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-700 rounded-full px-7 py-3 active:scale-95 disabled:opacity-50"
            data-testid="save-public-page-bottom"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function FormRow({ label, hint, icon, children }) {
  return (
    <div className="mb-4">
      <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
        {icon && <span className="text-slate-400">{icon}</span>}
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}
